import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  getReceipt,
  createReceipt,
  updateReceipt,
  postReceipt,
  unpostReceipt,
} from "../../../api/warehouseReceipts";
import { getProducts, ProductDto } from "../../../api/products";
import { useScanHandler } from "../../../context/ScannerContext";

import SupplierSelect from "./SupplierSelect";
import "../../../styles/warehouse/receipt-modal.css";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ru } from "date-fns/locale";

interface ReceiptItemRow {
  uid: number;
  productId: number;
  product: any;
  barcode: string;
  quantity: number;
  price: number;
}

interface Props {
  id: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const money = (v: number) => Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
let uidSeq = 1;

export default function ReceiptModal({ id, onClose, onSaved }: Props) {
  const [date, setDate] = useState<Date>(new Date());
  const [supplierId, setSupplierId] = useState<number | undefined>(undefined);
  const [items, setItems] = useState<ReceiptItemRow[]>([]);
  const [catalog, setCatalog] = useState<ProductDto[]>([]);
  const [term, setTerm] = useState("");
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(false);
  const addRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProducts().then((all) => setCatalog((all ?? []).filter((p) => p.type !== "service"))).catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    if (!id) {
      setItems([]);
      setSupplierId(undefined);
      setDate(new Date());
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  async function load() {
    const r = await getReceipt(id!);
    setDate(new Date(r.date));
    setPosted(!!r.posted);
    setSupplierId(r.supplierId ?? undefined);
    setItems(
      (r.items ?? []).map((it) => ({
        uid: uidSeq++,
        productId: it.productId,
        product: it.product,
        barcode: (it.product as any)?.barcode ?? "",
        quantity: it.quantity,
        price: it.price,
      }))
    );
  }

  // ── поиск товара для добавления: штрихкод, артикул или часть названия ──
  const suggestions = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (t.length < 2) return [];
    const parts = t.split(/\s+/).filter(Boolean);
    return catalog
      .filter((p) => {
        const hay = `${p.nameRu} ${p.sku ?? ""} ${p.barcode ?? ""} ${p.brand ?? ""} ${p.model ?? ""}`.toLowerCase();
        return parts.every((x) => hay.includes(x));
      })
      .slice(0, 8);
  }, [term, catalog]);

  function addProduct(p: ProductDto) {
    setItems((prev) => {
      const i = prev.findIndex((r) => r.productId === p.id);
      if (i >= 0) {
        const n = prev.slice();
        n[i] = { ...n[i], quantity: n[i].quantity + 1 };
        return n;
      }
      return [
        ...prev,
        { uid: uidSeq++, productId: p.id, product: p, barcode: p.barcode ?? "", quantity: 1, price: Number(p.purchasePrice) || 0 },
      ];
    });
    setFlash({ ok: true, text: `Добавлено: ${p.nameRu}` });
    setTerm("");
  }

  // Enter в строке поиска и код с телефона: точное совпадение по ШК/артикулу или единственный результат добавляем сразу
  function addByTerm(raw: string) {
    const t = raw.trim();
    if (!t) return;
    const low = t.toLowerCase();
    const exact = catalog.find(
      (p) => (p.barcode ?? "").toLowerCase() === low || (p.sku ?? "").toLowerCase() === low || (p.externalSkus ?? []).some((s) => s.toLowerCase() === low)
    );
    if (exact) { addProduct(exact); return; }
    const parts = low.split(/\s+/).filter(Boolean);
    const found = catalog.filter((p) => {
      const hay = `${p.nameRu} ${p.sku ?? ""} ${p.barcode ?? ""} ${p.brand ?? ""} ${p.model ?? ""}`.toLowerCase();
      return parts.every((x) => hay.includes(x));
    });
    if (found.length === 1) { addProduct(found[0]); return; }
    setTerm(t);
    setFlash({ ok: false, text: found.length ? `Найдено ${found.length}: выберите из списка` : `Товар «${t}» не найден. Заведите его в каталоге или введите ШК в карточке.` });
  }

  function updateItem(uid: number, patch: Partial<ReceiptItemRow>) {
    setItems((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  }

  function removeItem(uid: number) {
    setItems((prev) => prev.filter((r) => r.uid !== uid));
  }

  // код с телефона: если курсор в ячейке «ШК» строки, код записывается туда, иначе добавляем товар
  useScanHandler((code) => {
    const a = document.activeElement as HTMLElement | null;
    if (a && a.hasAttribute("data-rcbc")) {
      updateItem(Number(a.getAttribute("data-rcbc")), { barcode: code.replace(/\s+/g, "") });
      addRef.current?.focus();
    } else {
      addByTerm(code);
    }
  });

  const total = items.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.price) || 0), 0);

  function buildDto() {
    return {
      date: date.toISOString().slice(0, 10),
      supplierId: supplierId,
      items: items.map((i) => ({
        productId: i.productId,
        quantity: i.quantity,
        price: i.price,
        barcode: i.barcode.replace(/\s+/g, "") || undefined,
      })),
    };
  }

  function validate(): string | null {
    if (items.length === 0) return "Добавьте хотя бы одну позицию";
    const bad = items.find((i) => !(Number(i.quantity) >= 1));
    if (bad) return `«${bad.product?.nameRu ?? "Позиция"}»: количество должно быть не меньше 1`;
    return null;
  }

  async function submit(post: boolean) {
    const err = validate();
    if (err) { alert(err); return; }
    setBusy(true);
    try {
      const dto = buildDto();
      let receiptId = id;
      if (!receiptId) receiptId = (await createReceipt(dto)).id;
      else await updateReceipt(receiptId, dto);
      if (post) await postReceipt(receiptId);
      onSaved();
      onClose();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      alert(Array.isArray(m) ? m.join("\n") : m || "Не удалось сохранить документ");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnpost() {
    if (!id) return;
    if (!window.confirm("Снять документ с проведения? Остатки на складе уменьшатся на количество из документа.")) return;
    setBusy(true);
    try {
      await unpostReceipt(id);
      await load();
      onSaved();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join("\n") : msg || "Не удалось снять с проведения (возможно, товар уже продан)");
    } finally {
      setBusy(false);
    }
  }

  const focusAdd = () => addRef.current?.focus();
  const onCellEnter = (e: React.KeyboardEvent) => { if (e.key === "Enter") { e.preventDefault(); focusAdd(); } };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-window" style={{ maxWidth: 980, width: "96vw" }}>
        <div className="modal-header">
          <div className="modal-title">{id ? `Документ №${id}` : "Новый приход"}
            {posted && <span style={{ marginLeft: 10, fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "rgba(16,185,129,.15)", color: "#34d399", border: "1px solid rgba(16,185,129,.4)" }}>Проведён</span>}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <label>Дата документа</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <DatePicker
                selected={date}
                onChange={(d: Date | null) => d && setDate(d)}
                dateFormat="yyyy-MM-dd"
                locale={ru}
                className="primary-input"
                disabled={posted}
              />
              <button className="secondary-btn" disabled={posted} onClick={() => setDate(new Date())}>Сегодня</button>
              <button
                className="secondary-btn"
                disabled={posted}
                onClick={() => { const y = new Date(); y.setDate(y.getDate() - 1); setDate(y); }}
              >
                Вчера
              </button>
            </div>
          </div>

          <div className="form-row">
            <label>Поставщик</label>
            <div style={posted ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
              <SupplierSelect value={supplierId} onChange={setSupplierId} />
            </div>
          </div>

          {/* Добавление позиций: сканер (в том числе с телефона), артикул или название */}
          <div className="rc-add" style={{ position: "relative", display: posted ? "none" : undefined }}>
            <input
              ref={addRef}
              className="input"
              placeholder="Штрихкод, артикул или название: Enter добавит товар в таблицу"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addByTerm(term); } }}
              autoComplete="off"
            />
            {suggestions.length > 0 && (
              <div className="product-select-dropdown" style={{ position: "absolute", zIndex: 9999, left: 0, right: 0, top: "100%", maxHeight: 240, overflowY: "auto" }}>
                {suggestions.map((p) => (
                  <div key={p.id} className="product-select-item" onMouseDown={(e) => { e.preventDefault(); addProduct(p); focusAdd(); }}>
                    <span>{p.nameRu}</span>
                    <span style={{ color: "#6b7280", fontSize: 11, marginLeft: 6 }}>{[p.sku, p.barcode && `ШК ${p.barcode}`].filter(Boolean).join(" · ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {flash && <div className={`rc-flash ${flash.ok ? "" : "err"}`}>{flash.text}</div>}

          {posted && (
            <div className="rc-flash err" style={{ marginBottom: 8 }}>
              Документ проведён, поэтому только просмотр. Чтобы исправить, нажмите «Снять с проведения».
            </div>
          )}
          <fieldset disabled={posted} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          {/* Таблица с редактируемыми полями (десктоп) */}
          <div className="receipt-items-desktop">
            <table className="rc-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>№</th>
                  <th>Товар</th>
                  <th style={{ width: 150 }}>ШК</th>
                  <th style={{ width: 80 }}>Кол-во</th>
                  <th style={{ width: 110 }}>Цена, ₸</th>
                  <th style={{ width: 110, textAlign: "right" }}>Сумма, ₸</th>
                  <th style={{ width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} style={{ color: "#6b7280", textAlign: "center", padding: 18 }}>Отсканируйте штрихкод или найдите товар выше</td></tr>
                )}
                {items.map((it, idx) => (
                  <tr key={it.uid}>
                    <td>{idx + 1}</td>
                    <td className="rc-name">{it.product?.nameRu}<small>{it.product?.sku}</small></td>
                    <td>
                      <input data-rcbc={it.uid} inputMode="numeric" placeholder="нет ШК" value={it.barcode} onChange={(e) => updateItem(it.uid, { barcode: e.target.value.replace(/\s+/g, "") })} onKeyDown={onCellEnter} />
                    </td>
                    <td className="num">
                      <input type="number" min={1} value={it.quantity} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(it.uid, { quantity: Math.floor(+e.target.value) })} onKeyDown={onCellEnter} />
                    </td>
                    <td className="num">
                      <input type="number" min={0} value={it.price} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(it.uid, { price: +e.target.value })} onKeyDown={onCellEnter} />
                    </td>
                    <td style={{ textAlign: "right" }}>{money((Number(it.quantity) || 0) * (Number(it.price) || 0))}</td>
                    <td><button className="table-btn" onClick={() => removeItem(it.uid)} title="Убрать">×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* На телефоне — карточки */}
          <div className="receipt-items-mobile">
            {items.map((it, idx) => (
              <div key={it.uid} className="receipt-item-card">
                <div className="receipt-item-card-header">
                  <span className="receipt-item-num">{idx + 1}. {it.product?.nameRu}</span>
                  <button className="receipt-item-remove" onClick={() => removeItem(it.uid)}>✕</button>
                </div>
                <div className="receipt-item-row">
                  <div className="receipt-item-field" style={{ flex: "1 1 100%" }}>
                    <label>ШК</label>
                    <input data-rcbc={it.uid} inputMode="numeric" placeholder="нет ШК" value={it.barcode} onChange={(e) => updateItem(it.uid, { barcode: e.target.value.replace(/\s+/g, "") })} />
                  </div>
                </div>
                <div className="receipt-item-row">
                  <div className="receipt-item-field">
                    <label>Кол-во</label>
                    <input type="number" value={it.quantity} onChange={(e) => updateItem(it.uid, { quantity: Math.floor(+e.target.value) })} />
                  </div>
                  <div className="receipt-item-field">
                    <label>Цена</label>
                    <input type="number" value={it.price} onChange={(e) => updateItem(it.uid, { price: +e.target.value })} />
                  </div>
                  <div className="receipt-item-field">
                    <label>Сумма</label>
                    <div className="receipt-item-sum">{money((Number(it.quantity) || 0) * (Number(it.price) || 0))} ₸</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          </fieldset>

          <div className="rc-total">Итого: {money(total)} ₸</div>
        </div>

        <div className="modal-footer">
          {posted ? (
            <>
              <button className="secondary-btn" onClick={onClose}>Закрыть</button>
              <button className="secondary-btn danger-btn" disabled={busy} onClick={handleUnpost}>Снять с проведения</button>
            </>
          ) : (
            <>
              <button className="secondary-btn" disabled={busy} onClick={() => submit(false)}>Сохранить</button>
              <button className="primary-btn" disabled={busy} onClick={() => submit(true)}>Провести</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
